<template>
  <div>
    <AppLoader v-if="isLoading" class="text-center" small />
    <b-container fluid>
      <b-navbar toggleable="lg" type="dark" variant="light">
        <b-navbar-brand href="#" class="text-dark">
          <strong>Public Listed Page</strong>
        </b-navbar-brand>

        <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

        <b-collapse id="nav-collapse" is-nav>
          <!-- Right aligned nav items -->
          <b-navbar-nav class="ml-auto">
            <b-nav-form>
              <b-button
                variant="outline-primary"
                size="sm"
                @click="showModal = !showModal"
                class="mx-2"
              >
                <font-awesome-icon icon="plus" />
              </b-button>

              <b-button variant="outline-info" size="sm" @click="reloadTable">
                <font-awesome-icon icon="sync" />
              </b-button>
              <b-form-group
                label-cols-sm="3"
                label-align-sm="right"
                label-size="sm"
                label-for="filterInput"
              >
                <b-input-group size="sm">
                  <b-form-input
                    v-model="filter"
                    type="search"
                    id="filterInput"
                    placeholder="Type to Search"
                  ></b-form-input>
                  <b-input-group-append>
                    <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                  </b-input-group-append>
                </b-input-group>
              </b-form-group>
            </b-nav-form>
          </b-navbar-nav>
        </b-collapse>
      </b-navbar>

      <b-table
        class="mx-1"
        bordered
        sticky-header="calc(100vh - 200px)"
        ref="table"
        id="my-table"
        :busy.sync="isLoading"
        :items="getPublicListingCompanyTableData"
        :fields="tableFields"
        :filter="filter"
        :per-page="perPage"
        :current-page="currentPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        responsive="sm"
      >
        <template v-slot:cell(actions)="data">
          <div class="text-nowrap">
            <b-button
              size="sm"
              class="mr-2"
              type="button"
              variant="outline-info"
              @click="onEditPublicListedCompany(data)"
            >
              <font-awesome-icon v-b-tooltip.hover title="Edit Field" icon="edit" />
            </b-button>
            <b-button
              size="sm"
              type="button"
              variant="outline-danger"
              @click="onDeletePublicListedCompanyModal(data)"
            >
              <font-awesome-icon v-b-tooltip.hover title="Delete Field" icon="trash" />
            </b-button>
          </div>
        </template>
      </b-table>
      <b-row>
        <b-col cols="2">
          <b-form-select v-model="perPage" id="perPageSelect" size="sm" :options="pageOptions"></b-form-select>
        </b-col>
        <b-col>
          <b-pagination
            class="float-right"
            v-model="currentPage"
            :total-rows="rows"
            :per-page="perPage"
            first-text="First"
            prev-text="Prev"
            next-text="Next"
            last-text="Last"
            aria-controls="my-table"
          ></b-pagination>
        </b-col>
      </b-row>
    </b-container>
    <b-modal
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      size="lg"
      :title="editPublicListedCompany ? 'Update Public Listed Company' : 'Create Public Listed Company'"
      v-model="showModal"
    >
      <div>
        <div v-if="!submiting && !responseSuccess">
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Name :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  v-model="$v.form.name.$model"
                  :state="$v.form.name.$dirty ? !$v.form.name.$error : null"
                  placeholder="Enter Name"
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Local Name :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  v-model="$v.form.localName.$model"
                  :state="$v.form.localName.$dirty ? !$v.form.localName.$error : null"
                  placeholder="Enter Local Name"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Country :
                  <span class="required-span">*</span>
                </label>
                <v-select
                  size="sm"
                  :class="$v.form.country.$error ? 'border border-danger rounded' : null"
                  :searchable="true"
                  :clearable="true"
                  label="name"
                  v-model="$v.form.country.$model"
                  :state="$v.form.country.$dirty ? !$v.form.country.$error : null"
                  placeholder="-- Select Country --"
                  :options="countriesOptions"
                  @input="setCountryValue"
                >
                  <template slot="option" slot-scope="option">
                    <country-flag v-if="option.icon" :country="option.icon" size="small" />
                    <span>{{ '&nbsp;'+ option.name }}</span>
                  </template>
                </v-select>
              </b-form-group>
            </b-col>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Stock Exchange :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  v-model="$v.form.stockExchange.$model"
                  :state="$v.form.stockExchange.$dirty ? !$v.form.stockExchange.$error : null"
                  placeholder="Enter Stock Exchange"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="6">
              <b-form-group>
                <label>
                  Stock Exchange Code :
                  <span class="required-span">*</span>
                </label>
                <b-form-input
                  v-model="$v.form.stockExchangeCode.$model"
                  :state="$v.form.stockExchangeCode.$dirty ? !$v.form.stockExchangeCode.$error : null"
                  placeholder="Enter Stock Exchange Code"
                ></b-form-input>
              </b-form-group>
            </b-col>
          </b-row>
        </div>
        <div v-else-if="submiting && !responseSuccess">
          <p>{{editPublicListedCompany ? 'Your Public Listed Company is being Updating......' : 'Your Public Listed Company is being Creating......'}}</p>
          <b-spinner type="grow" label="Loading..."></b-spinner>
        </div>
        <div v-else>
          <p class="my-2 text-center">
            {{ editPublicListedCompany ? 'Your Public Listed Company has been updated successfully.' : 'Your Public Listed Company has been added successfully.' }}
            <br />
          </p>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button v-if="!submiting" variant="outline-danger" @click="resetModal">Close</b-button>
        <b-button
          v-if="!submiting && !responseSuccess"
          variant="outline-success"
          @click="checkValidation()"
        >{{ editPublicListedCompany ? 'Update' : 'Save' }}</b-button>
      </template>
    </b-modal>
    <b-modal
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      title="Delete Field"
      v-model="showDeletePublicListedCompanyModal"
    >
      <div>
        <div v-if="!submiting && !responseSuccess">
          <p>Are you sure you want to delete this Public Listed Company ?</p>
        </div>
        <div v-else-if="submiting && !responseSuccess">
          <p>Public Listed Company is being deleting......</p>
          <b-spinner type="grow" label="Loading..."></b-spinner>
        </div>
        <div v-else>
          <p class="my-2 text-center">
            Public Listed Company has been deleted successfully.
            <br />
          </p>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button v-if="!submiting" @click="resetDeleteModal">Close</b-button>
        <b-button
          v-if="!submiting && !responseSuccess"
          variant="danger"
          @click="deletePublicListedCompanyCall"
        >
          <font-awesome-icon
            v-b-tooltip.hover
            title="Delete Field"
            class="mr-1 text-light"
            icon="trash"
          />Yes
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
<script>
import { HTTP } from "../../utils/HTTP";
import AppLoader from "../../components/Loader/Loader";
import { validationMixin } from "vuelidate";
import { required } from "vuelidate/lib/validators";
import CountryCodes from "../../utils/countryCodes.json";
export default {
  name: "clientProfilePublicListedCompany",
  mixins: [validationMixin],
  components: {
    AppLoader
  },
  data() {
    return {
      form: {
        name: null,
        localName: null,
        country: null,
        stockExchange: null,
        stockExchangeCode: null
      },
      publicListedCompanyId: null,
      showDeletePublicListedCompanyModal: false,
      countriesOptions: CountryCodes.countries,
      editPublicListedCompany: false,
      isLoading: false,
      showModal: false,
      tableFields: [
        { key: "created_at", label: "Age", sortable: true },
        { key: "name", label: "Name", sortable: true },
        { key: "local_name", label: "Local Name", sortable: true },
        { key: "country", label: "Country", sortable: true },
        { key: "stock_exchange", label: "Stock Exchange", sortable: true },
        {
          key: "stock_exchange_code",
          label: "Stock Exchange Code",
          sortable: true
        },
        { key: "actions", label: "Actions", sortable: false }
      ],
      filter: null,
      perPage: 10,
      totalRows: 0,
      currentPage: 1,
      sortBy: "id",
      sortDesc: true,
      pageOptions: [5, 10, 25, 50, 75, 100],
      responseSuccess: false,
      submiting: false,
      countryValue: null
    };
  },
  mounted() {},
  computed: {
    rows() {
      return this.totalRows;
    }
  },
  validations: {
    form: {
      name: { required },
      localName: { required },
      country: { required },
      stockExchange: { required },
      stockExchangeCode: { required }
    }
  },
  methods: {
    reloadTable() {
      this.$root.$emit("bv::refresh::table", "my-table");
    },
    async getPublicListingCompanyTableData() {
      let formData = {
        filter: this.filter,
        sort: this.sortBy + "|" + (this.sortDesc == true ? "desc" : "asc"),
        per_page: this.perPage,
        page: this.currentPage
      };

      let token = "Bearer " + localStorage.getItem("accessToken");
      this.isLoading = true;
      let result = HTTP(
        "post",
        "v1/google-custom-search-engine/get-public-listed-company-list",
        formData,
        {
          Authorization: token
        }
      );

      return result
        .then(response => {
          const items = response.data.data;
          this.isLoading = false;
          this.totalRows = response.data.total;
          return items;
        })
        .catch(error => {
          this.isLoading = false;
          return [error];
        });
    },
    resetForm() {
      this.form = {
        name: null,
        localName: null,
        country: null,
        stockExchange: null,
        stockExchangeCode: null
      };
      this.countryValue = null;
      this.$v.form.$reset();
    },
    resetModal() {
      this.resetForm();
      this.editPublicListedCompany = false;
      this.responseSuccess = false;
      this.submiting = false;
      this.isLoading = false;
      this.showModal = false;
    },
    setCountryValue() {
      this.countryValue = this.form.country.name;
    },
    checkValidation() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      } else if (this.editPublicListedCompany) {
        this.updatePublicListedCompany().then(this.reloadTable);
      } else {
        this.savePublicListingCompany().then(this.reloadTable);
      }
    },
    async savePublicListingCompany() {
      let formData = {
        name: this.form.name,
        local_name: this.form.localName,
        country: this.countryValue,
        stock_exchange: this.form.stockExchange,
        stock_exchange_code: this.form.stockExchangeCode
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP(
          "post",
          "v1/google-custom-search-engine/create-public-listed-company",
          formData,
          {
            Authorization: token
          }
        );
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (err) {
        return [err];
        // // console.log("------------erroror", err);
      }
    },
    onEditPublicListedCompany(data) {
      this.editPublicListedCompany = true;
      this.publicListedCompanyId = data.item.id;
      this.form = {
        name: data.item.name,
        localName: data.item.local_name,
        country: data.item.country,
        stockExchange: data.item.stock_exchange,
        stockExchangeCode: data.item.stock_exchange_code
      };
      this.countryValue = data.item.country;
      this.showModal = true;
    },
    async updatePublicListedCompany() {
      let formData = {
        public_listed_company_id: this.publicListedCompanyId,
        name: this.form.name,
        local_name: this.form.localName,
        country: this.countryValue,
        stock_exchange: this.form.stockExchange,
        stock_exchange_code: this.form.stockExchangeCode
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP(
          "post",
          "v1/google-custom-search-engine/update-public-listed-company",
          formData,
          {
            Authorization: token
          }
        );
        this.publicListedCompanyId = null;
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (err) {
        return [err];
        // // console.log("------------erroror", err);
      }
    },
    onDeletePublicListedCompanyModal(data) {
      this.publicListedCompanyId = data.item.id;
      this.showDeletePublicListedCompanyModal = true;
    },
    deletePublicListedCompanyCall() {
      this.deletePublicListedCompany().then(this.reloadTable);
    },
    async deletePublicListedCompany() {
      this.isLoading = true;
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP(
          "get",
          "v1/google-custom-search-engine/deletepubliclistedcompany/" +
            this.publicListedCompanyId,
          null,
          {
            Authorization: token
          }
        );
        this.publicListedCompanyId = null;
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (error) {
        this.isLoading = false;
      }
    },
    resetDeleteModal() {
      this.submiting = false;
      this.isLoading = false;
      this.responseSuccess = false;
      this.showDeletePublicListedCompanyModal = false;
    }
  }
};
</script>